import React from "react";
import { Button } from "@material-ui/core";
import { ArrowBackSharp } from "@material-ui/icons";
import { DocusealBuilder } from "@docuseal/react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { withRouter, RouteComponentProps } from "react-router-dom";

import { createNewTemplate, editTemplate, updateTemplate } from "./queries";
import { useSubscription } from "../../../../../framework/src/contexts/Subscription";
import withToast, {
  withToastProps
} from "../../../../../components/src/withSnackBar.Web";

const DOCUSEAL_BASE_URL = window._env_ ? window._env_.REACT_APP_DOCUSEAL_BASE_URL : 'localhost';

type TemplateEditorProps = RouteComponentProps & withToastProps & {};

const TemplateEditor = ({
  history,
  location,
  showToast
}: TemplateEditorProps) => {
  const { authToken } = useSubscription();
  const params = new URLSearchParams(location.search);

  const mode = params.get("mode");
  const templateId = params.get("templateId");
  const preview = params.get("preview");

  const create = useQuery({
    queryKey: ["createNewTemplate"],
    queryFn: () => createNewTemplate(authToken),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    enabled: !mode && !templateId,
    onError: (err: AxiosError) => {
      if (err.status === 400) {
        showToast({
          type: "error",
          message: "Login expired, please sign in again"
        });
      } else {
        showToast({
          type: "error",
          message: "Failed to create new template, please try again"
        });
      }
    }
  });

  const edit = useQuery({
    queryKey: ["editTemplate", templateId],
    queryFn: () => editTemplate(authToken, templateId!),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    enabled: mode === "edit" && Boolean(templateId),
    onError: (err: AxiosError) => {
      if (err.status === 400) {
        showToast({
          type: "error",
          message: "Login expired, please sign in again"
        });
      } else {
        showToast({
          type: "error",
          message: "Failed to open your template for editing, please try again"
        });
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (template: any) => {
      updateTemplate(
        authToken,
        template.external_id,
        template.name,
        template.id
      );
    },
    onSuccess: () => {
      showToast({
        type: "success",
        message: "Saved automatically"
      });
    },
    onError: () => {
      showToast({
        type: "warning",
        message: "Last save attempt failed"
      });
    }
  });

  return (
    <>
      <Button
        color="secondary"
        startIcon={<ArrowBackSharp />}
        onClick={() => history.push("/home/settings/documentTemplates")}
      >
        Back
      </Button>
      {!create.isLoading && !create.isError && create.data.token && (
        <DocusealBuilder
          host={DOCUSEAL_BASE_URL}
          token={create.data.token}
          withSendButton={false}
          withSignYourselfButton={false}
          onSave={template => {
            updateMutation.mutate(template);
          }}
        />
      )}
      {!edit.isLoading && !edit.isError && edit.data.token && (
        <DocusealBuilder
          host={DOCUSEAL_BASE_URL}
          token={edit.data.token}
          preview={preview === "true"}
          withSendButton={false}
          withSignYourselfButton={false}
          onSave={template => {
            updateMutation.mutate(template);
          }}
        />
      )}
    </>
  );
};

export default withToast(withRouter(TemplateEditor));
